// app-pages/AdminPage/StylePanel.tsx
"use client";
import {
  POST_CREATE_STYLE,
  POST_UPDATE_STYLE,
  DELETE_STYLE,
  POST_CREATE_OPTION,
  POST_UPDATE_OPTION,
  DELETE_OPTION,
  POST_ADD_CONTROLNET_IMAGES_TO_OPTION,
  DELETE_CONTROLNET_IMAGE,
} from "api";

import {
  Flex,
  Text,
  Box,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Editable,
  EditableTextarea,
  EditablePreview,
  Input,
  Textarea,
  VStack,
  useToast,
  Stack,
  RadioGroup,
  Radio,
  Checkbox,
  HStack,
  Switch,
  Image,
  IconButton,
  Icon,
} from "@chakra-ui/react";

import React, { useState, useEffect } from "react";
import { fetch } from "libs";
import { useQueryClient } from "@tanstack/react-query";

import {
  useGetAllInternalOptionCategories,
  useGetAllInternalModelCategories,
  useGetAllInternalStyles,
} from "queries";

import {
  InternalOptionCategory,
  InternalOption,
  ControlnetImage,
  InternalModelCategory,
  InternalStyle,
} from "types";

import { useDropzone } from "react-dropzone";
import { LuImagePlus } from "react-icons/lu";
import { DeleteIcon } from "@chakra-ui/icons";
import { FaTrash } from "react-icons/fa";

type FileWithPreview = File & {
  file: File;
  preview: string;
};

type DropzoneProps = {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
};

const Dropzone: React.FC<DropzoneProps> = ({ files, setFiles }) => {
  const onDrop = (acceptedFiles: File[]) => {
    const newFilesWithPreview = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    })) as FileWithPreview[];

    setFiles((prevFiles) => [...prevFiles, ...newFilesWithPreview]);
  };

  const removeFile = (name: string) => {
    setFiles((prevFiles: FileWithPreview[]) => {
      console.log(prevFiles);
      const newFiles = prevFiles.filter((file: FileWithPreview) => file.file.name !== name);
      // Revoke the URL of the removed file to avoid memory leaks
      const removedFile = prevFiles.find((file: FileWithPreview) => file.file.name === name);
      if (removedFile) URL.revokeObjectURL(removedFile.file.name);
      return newFiles;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const thumbs = (files: FileWithPreview[]) =>
    files.map((file) => (
      <Box
        key={file.file.name}
        p="2"
        display="inline-block"
        position="relative"
        borderRadius="lg"
        overflow="hidden"
      >
        <Image src={file.preview} alt={file.file.name} w="100px" h="100px" />
        <IconButton
          aria-label="Delete image"
          icon={<DeleteIcon />}
          size="sm"
          colorScheme="red"
          position="absolute"
          right="2"
          bottom="2"
          onClick={(e) => {
            e.stopPropagation();
            removeFile(file.file.name);
          }}
        />
      </Box>
    ));

  return (
    <div
      {...getRootProps()}
      style={{ marginTop: "20px", border: "2px dashed gray", padding: "20px", textAlign: "center" }}
    >
      <input {...getInputProps()} />

      <Flex minH="200px" direction="column" w="100%" align="center" justify="center">
        {isDragActive && !files.length ? (
          <Text> Drop your files here!</Text>
        ) : (
          <>
            <LuImagePlus size={35} />
            <Text mt="3px" color="#1D71F9" fontWeight={700}>
              Upload files or drag & drop
            </Text>
            <Text mt="3px">PNG, JPG, WEBP, and HEIC up to 15MB</Text>
            <aside style={{ display: "flex", flexWrap: "wrap" }}>{thumbs(files)}</aside>
            <Text fontWeight={700} color="#E20FCE">
              {files.length
                ? `${files.length} ControlNet images uploaded`
                : "Upload ControlNet images here"}
            </Text>
          </>
        )}
      </Flex>
    </div>
  );
};

export const StylePanel: React.FC = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: internalOptionCategories, isLoading: isInternalOptionCategoriesLoading } =
    useGetAllInternalOptionCategories();

  const { data: internalModelCategories, isLoading: isInternalModelCategoriesLoading } =
    useGetAllInternalModelCategories();

  const { data: internalStyles, isLoading: isInternalStylesLoading } = useGetAllInternalStyles();

  const [selectedInternalStyle, setSelectedInternalStyle] = useState<InternalStyle | null>(null);
  const [selectedInternalOption, setSelectedInternalOption] = useState<InternalOption | null>(null);

  const [updateStylePrompt, setUpdateStylePrompt] = useState<string>("");
  const [updateStyleNegativePrompt, setUpdateStyleNegativePrompt] = useState<string>("");
  const [updateStyleSettings, setUpdateStyleSettings] = useState<string>("{}");

  const [createStyleName, setCreateStyleName] = useState<string>("");
  const [createStyleModelCategoryName, setCreateStyleModelCategoryName] = useState<string>("");
  const [createStylePrompt, setCreateStylePrompt] = useState<string>("");
  const [createStyleNegativePrompt, setCreateStyleNegativePrompt] = useState<string>("");
  const [createStyleSettings, setCreateStyleSettings] = useState<string>("{}");

  const [createOptionName, setCreateOptionName] = useState<string>("");
  const [createOptionSelectedOptionCategory, setCreateOptionSelectedOptionCategory] =
    useState<InternalOptionCategory | null>(null);
  const [createOptionPrompt, setCreateOptionPrompt] = useState<string>("");
  const [createOptionNegativePrompt, setCreateOptionNegativePrompt] = useState<string>("");
  const [createOptionNSFW, setCreateOptionNSFW] = useState<boolean>(false);
  const [createOptionSettings, setCreateOptionSettings] = useState<string>("{}");

  const [updateOptionPrompt, setUpdateOptionPrompt] = useState<string>("");
  const [updateOptionNegativePrompt, setUpdateOptionNegativePrompt] = useState<string>("");
  const [updateOptionNSFW, setUpdateOptionNSFW] = useState<boolean>(false);
  const [updateOptionSettings, setUpdateOptionSettings] = useState<string>("{}");
  const [isQuickMode, setIsQuickMode] = useState<boolean>(false);
  const [controlnetImageUploadFiles, setControlnetImageUploadFiles] = useState<any>([]);

  const {
    isOpen: internalStyleIsOpen,
    onOpen: internalStyleOnOpen,
    onClose: internalStyleOnClose,
  } = useDisclosure();

  const {
    isOpen: internalOptionIsOpen,
    onOpen: internalOptionOnOpen,
    onClose: internalOptionOnClose,
  } = useDisclosure();

  useEffect(() => {
    if (internalStyles) {
      setSelectedInternalStyle(null);
      setSelectedInternalOption(null);
    }
  }, [internalStyles]);

  useEffect(() => {
    if (internalModelCategories && internalModelCategories.model_categories.length > 0) {
      setCreateStyleModelCategoryName(internalModelCategories.model_categories[0].name);
    }
  }, [internalModelCategories]);

  useEffect(() => {
    if (internalOptionCategories) {
      setCreateOptionSelectedOptionCategory(
        internalOptionCategories.option_categories.find(
          (internalOptionCategory: InternalOptionCategory) =>
            isQuickMode === internalOptionCategory.quick_mode
        ) ?? null
      );
    }
  }, [internalOptionCategories, isQuickMode]);

  useEffect(() => {
    if (selectedInternalStyle) {
      setUpdateStylePrompt(selectedInternalStyle.prompt);
      setUpdateStyleNegativePrompt(selectedInternalStyle.negative_prompt);
      setUpdateStyleSettings(JSON.stringify(selectedInternalStyle.settings));
    }
  }, [selectedInternalStyle]);

  useEffect(() => {
    if (selectedInternalOption) {
      setUpdateOptionPrompt(selectedInternalOption.prompt);
      setUpdateOptionNegativePrompt(selectedInternalOption.negative_prompt);
      setUpdateOptionNSFW(selectedInternalOption.nsfw);
      setUpdateOptionSettings(JSON.stringify(selectedInternalOption.settings));
    }
  }, [selectedInternalOption]);

  const handleInternalStyleClick = (internalStyle: InternalStyle) => {
    setSelectedInternalStyle(internalStyle);
    internalStyleOnOpen();
  };

  const handleInternalOptionClick = (internalOption: InternalOption) => {
    setSelectedInternalOption(internalOption);
    internalOptionOnOpen();
  };

  const handleInternalStyleCreate = async () => {
    try {
      if (
        !createStyleName ||
        !createStyleModelCategoryName ||
        !createStylePrompt ||
        !createStyleSettings
      )
        return;

      const settings = JSON.parse(createStyleSettings);

      const styleData = {
        name: createStyleName,
        model_category_name: createStyleModelCategoryName,
        prompt: createStylePrompt,
        negative_prompt: createStyleNegativePrompt,
        settings: settings,
      };

      const response = await fetch(POST_CREATE_STYLE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(styleData),
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Style created successfully", response);
      setCreateStyleName("");

      if (internalModelCategories && internalModelCategories.model_categories.length > 0) {
        setCreateStyleModelCategoryName(internalModelCategories.model_categories[0].name);
      }

      setCreateStylePrompt("");
      setCreateStyleNegativePrompt("");
      setCreateStyleSettings("{}");

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error creating style:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalStyleUpdate = async () => {
    try {
      if (!selectedInternalStyle) return;
      const settings = JSON.parse(updateStyleSettings);

      const styleData = {
        prompt: updateStylePrompt,
        negative_prompt: updateStyleNegativePrompt,
        settings: settings,
      };

      const response = await fetch(POST_UPDATE_STYLE(selectedInternalStyle.id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(styleData),
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Style updated successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating style:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalStyleDelete = async (force_delete: boolean = false) => {
    try {
      if (!selectedInternalStyle) return;

      const response = await fetch(
        `${DELETE_STYLE(selectedInternalStyle.id)}?force_delete=${force_delete}`,
        {
          method: "DELETE",
        }
      );

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Style deleted successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting style:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalOptionCreate = async () => {
    try {
      if (
        !selectedInternalStyle ||
        !createOptionName ||
        !createOptionSelectedOptionCategory ||
        !createOptionPrompt
      ) {
        return;
      }

      const settings = JSON.parse(createOptionSettings);
      const formData = new FormData();

      const optionData = {
        name: createOptionName,
        style_name: selectedInternalStyle.name,
        model_category_name: selectedInternalStyle.model_category?.name,
        option_category_name: createOptionSelectedOptionCategory.name,
        option_category_quick_mode: createOptionSelectedOptionCategory.quick_mode,
        prompt: createOptionPrompt,
        negative_prompt: createOptionNegativePrompt,
        nsfw: createOptionNSFW,
        settings: settings,
      };

      formData.append("option_data", JSON.stringify(optionData));

      if (controlnetImageUploadFiles.length > 0) {
        controlnetImageUploadFiles.forEach((file: any) =>
          formData.append("controlnet_image_files", file.file)
        );
      }

      const response = await fetch(POST_CREATE_OPTION, {
        method: "POST",
        body: formData,
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option created successfully", response);

      setCreateOptionName("");

      if (internalOptionCategories) {
        setCreateOptionSelectedOptionCategory(
          internalOptionCategories.option_categories.find(
            (internalOptionCategory: InternalOptionCategory) =>
              isQuickMode === internalOptionCategory.quick_mode
          ) ?? null
        );
      }

      setCreateOptionPrompt("");
      setCreateOptionNegativePrompt("");
      setCreateOptionNSFW(false);
      setCreateOptionSettings("{}");
      setControlnetImageUploadFiles([]);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error creating option:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalOptionUpdate = async () => {
    try {
      if (!selectedInternalOption) return;

      const settings = JSON.parse(updateOptionSettings);

      const optionData = {
        prompt: updateOptionPrompt,
        negative_prompt: updateOptionNegativePrompt,
        nsfw: updateOptionNSFW,
        settings: settings,
      };

      const response = await fetch(POST_UPDATE_OPTION(selectedInternalOption.id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(optionData),
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option updated successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating option:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInternalOptionDelete = async (force_delete: boolean = false) => {
    try {
      if (!selectedInternalOption) return;

      const response = await fetch(
        `${DELETE_OPTION(selectedInternalOption.id)}?force_delete=${force_delete}`,
        {
          method: "DELETE",
        }
      );

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Option deleted successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting option:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddControlnetImageToOption = async () => {
    try {
      if (!selectedInternalOption) return;
      if (controlnetImageUploadFiles.length === 0) return;
      const formData = new FormData();

      controlnetImageUploadFiles.forEach((file: any) =>
        formData.append("controlnet_image_files", file.file)
      );

      const response = await fetch(
        POST_ADD_CONTROLNET_IMAGES_TO_OPTION(selectedInternalOption.id),
        {
          method: "POST",
          body: formData,
        }
      );

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Added controlnet image to option successfully", response);
      setControlnetImageUploadFiles([]);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error adding controlnet image to option:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleControlnetImageDelete = async (file_name: string) => {
    try {
      if (!selectedInternalOption) return;

      const response = await fetch(DELETE_CONTROLNET_IMAGE(file_name), {
        method: "DELETE",
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({
        queryKey: ["internalStyles"],
      });

      console.log("Controlnet image deleted successfully", response);

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting controlnet image:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex w="100%" direction="column" alignItems="center">
      <Text fontWeight="semibold" fontSize="24px" mt="20px">
        Style Panel
      </Text>
      {!isInternalStylesLoading && (
        <Flex w="80%" direction="row">
          <Flex direction="column" w="70%">
            <Text fontWeight="semibold" fontSize="20px" mt="16px">
              Current styles
            </Text>
            <Flex wrap="wrap" mt={4}>
              {internalStyles?.styles.map((internalStyle: InternalStyle, index: number) => (
                <Box
                  ml={index > 0 ? "5px" : ""}
                  key={internalStyle.id}
                  border={
                    internalStyle.id === selectedInternalStyle?.id
                      ? "1px solid white"
                      : "1px solid gray"
                  }
                  p={2}
                  onClick={() => handleInternalStyleClick(internalStyle)}
                >
                  <Text color="white">{`${internalStyle.name} - ${internalStyle.model_category?.name}`}</Text>
                </Box>
              ))}
            </Flex>
            {selectedInternalStyle && (
              <Modal
                isOpen={internalStyleIsOpen}
                onClose={() => {
                  internalStyleOnClose();
                  setSelectedInternalStyle(null);
                }}
                size="full"
              >
                <ModalOverlay />
                <ModalContent color="black" bgColor="white">
                  <ModalHeader>{`Style: ${selectedInternalStyle.name} - ${selectedInternalStyle.model_category?.name}`}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Text>{`ID: ${selectedInternalStyle.id}`}</Text>
                    <Text>{`Name: ${selectedInternalStyle.name}`}</Text>
                    <Text>{`Model category: ${selectedInternalStyle.model_category?.name}`}</Text>
                    <Flex direction="row" alignItems="center">
                      <Text mr={2}>Prompt:</Text>
                      <Editable
                        value={updateStylePrompt}
                        onChange={(value) => setUpdateStylePrompt(value)}
                        w="100%"
                      >
                        <EditablePreview width="full" minH="50px" border="1px" />
                        <EditableTextarea />
                      </Editable>
                    </Flex>
                    <Flex direction="row" alignItems="center">
                      <Text mr={2}>Negative prompt:</Text>
                      <Editable
                        value={updateStyleNegativePrompt}
                        onChange={(value) => setUpdateStyleNegativePrompt(value)}
                        w="100%"
                      >
                        <EditablePreview width="full" minH="50px" border="1px" />
                        <EditableTextarea />
                      </Editable>
                    </Flex>
                    <Flex direction="row" alignItems="center">
                      <Text mr={2}>Settings:</Text>
                      <Editable
                        value={updateStyleSettings}
                        onChange={(value) => setUpdateStyleSettings(value)}
                        w="100%"
                      >
                        <EditablePreview width="full" minH="50px" border="1px" />
                        <EditableTextarea />
                      </Editable>
                    </Flex>
                    <Flex direction="row">
                      <Flex direction="column" w="70%">
                        <Switch
                          size="md"
                          mt={4}
                          mb={4}
                          isChecked={isQuickMode}
                          onChange={(e) => setIsQuickMode(e.target.checked)}
                        >
                          Quick Mode Toggle
                        </Switch>
                        {selectedInternalStyle.option_categories.map(
                          (internalOptionCategory: InternalOptionCategory) =>
                            isQuickMode === internalOptionCategory.quick_mode && (
                              <Flex direction="column">
                                <Text>{`Option category: ${internalOptionCategory.name}${
                                  internalOptionCategory.quick_mode
                                    ? " (Quick Mode)"
                                    : " (Normal Mode)"
                                } 's option(s)`}</Text>
                                <Flex wrap="wrap" mt={4} mb={4}>
                                  {internalOptionCategory.options.map(
                                    (internalOption: InternalOption, index: number) => (
                                      <Box
                                        ml={index > 0 ? "5px" : ""}
                                        key={internalOption.id}
                                        border={
                                          internalOption.id === selectedInternalOption?.id
                                            ? "1px solid black"
                                            : "1px solid gray"
                                        }
                                        p={2}
                                        onClick={() => handleInternalOptionClick(internalOption)}
                                      >
                                        <Text>{`${internalOption.name}`}</Text>
                                      </Box>
                                    )
                                  )}
                                </Flex>
                              </Flex>
                            )
                        )}
                        {selectedInternalOption && (
                          <Modal
                            isOpen={internalOptionIsOpen}
                            onClose={() => {
                              internalOptionOnClose();
                              setSelectedInternalOption(null);
                            }}
                            size="6xl"
                          >
                            <ModalOverlay />
                            <ModalContent color="black" bgColor="white">
                              <ModalHeader>{`Option: ${selectedInternalOption.name}`}</ModalHeader>
                              <ModalCloseButton />
                              <ModalBody>
                                <Text>{`ID: ${selectedInternalOption.id}`}</Text>
                                <Text>{`Name: ${selectedInternalOption.name}`}</Text>
                                <Flex direction="row" alignItems="center">
                                  <Text mr={2}>Prompt:</Text>
                                  <Editable
                                    value={updateOptionPrompt}
                                    onChange={(value) => setUpdateOptionPrompt(value)}
                                    w="100%"
                                  >
                                    <EditablePreview width="full" minH="50px" border="1px" />
                                    <EditableTextarea />
                                  </Editable>
                                </Flex>
                                <Flex direction="row" alignItems="center">
                                  <Text mr={2}>Negative prompt:</Text>
                                  <Editable
                                    value={updateOptionNegativePrompt}
                                    onChange={(value) => setUpdateOptionNegativePrompt(value)}
                                    w="100%"
                                  >
                                    <EditablePreview width="full" minH="50px" border="1px" />
                                    <EditableTextarea />
                                  </Editable>
                                </Flex>
                                <Checkbox
                                  isChecked={updateOptionNSFW}
                                  onChange={(e) => setUpdateOptionNSFW(e.target.checked)}
                                >
                                  NSFW
                                </Checkbox>
                                {isQuickMode && (
                                  <>
                                    <Flex direction="row" alignItems="center">
                                      <Text mr={2}>
                                        Settings (If not {"{}"}, override the entire style
                                        settings):
                                      </Text>
                                      <Editable
                                        value={updateOptionSettings}
                                        onChange={(value) => setUpdateOptionSettings(value)}
                                        w="100%"
                                      >
                                        <EditablePreview width="full" minH="50px" border="1px" />
                                        <EditableTextarea />
                                      </Editable>
                                    </Flex>
                                    <Flex direction="column" w="100%">
                                      <Text fontWeight="bold">ControlNet images</Text>
                                      <Dropzone
                                        files={controlnetImageUploadFiles}
                                        setFiles={setControlnetImageUploadFiles}
                                      />
                                      <Button
                                        mt="10px"
                                        colorScheme="blue"
                                        onClick={handleAddControlnetImageToOption}
                                      >
                                        Upload
                                      </Button>
                                      <Flex direction="row" w="100%" mt="25px">
                                        {selectedInternalOption.controlnet_images.map(
                                          (controlnet_image: ControlnetImage) => (
                                            <Flex position="relative">
                                              <Image
                                                w="132px"
                                                src={controlnet_image.sas_url}
                                                objectFit="cover"
                                              />
                                              <Button
                                                size="xs"
                                                colorScheme="red"
                                                position="absolute"
                                                bottom={2} // Position the button at the bottom of the container
                                                right={2} // And to the right
                                                onClick={(e) => {
                                                  e.stopPropagation(); // Prevent click event from bubbling to the image click handler
                                                  handleControlnetImageDelete(
                                                    controlnet_image.file_name
                                                  );
                                                }}
                                              >
                                                <Icon as={FaTrash} boxSize={3} />
                                              </Button>
                                            </Flex>
                                          )
                                        )}
                                      </Flex>
                                    </Flex>
                                  </>
                                )}
                              </ModalBody>
                              <ModalFooter>
                                <Button
                                  colorScheme="green"
                                  mr={3}
                                  onClick={() => handleInternalOptionUpdate()}
                                >
                                  Update
                                </Button>
                                <Button
                                  colorScheme="red"
                                  mr={3}
                                  onClick={() => handleInternalOptionDelete(false)}
                                >
                                  Delete
                                </Button>
                                <Button
                                  colorScheme="red"
                                  mr={3}
                                  onClick={() => handleInternalOptionDelete(true)}
                                >
                                  Force Delete
                                </Button>
                                <Button
                                  colorScheme="blue"
                                  mr={3}
                                  onClick={() => {
                                    internalOptionOnClose();
                                    setSelectedInternalOption(null);
                                  }}
                                >
                                  Close
                                </Button>
                              </ModalFooter>
                            </ModalContent>
                          </Modal>
                        )}
                      </Flex>
                      {!isInternalOptionCategoriesLoading && (
                        <Flex direction="column" w="30%">
                          <Text fontWeight="semibold" fontSize="20px" mt="16px">
                            Create {isQuickMode ? "quick mode " : "normal mode "}option for this
                            style
                          </Text>
                          <VStack spacing="4" align="stretch" mt="5">
                            <Input
                              placeholder="Name"
                              value={createOptionName}
                              onChange={(e) => setCreateOptionName(e.target.value)}
                            />
                            <Stack direction="row" alignItems="center">
                              <Text fontWeight={700}>Option category</Text>
                              <RadioGroup
                                onChange={(id) =>
                                  setCreateOptionSelectedOptionCategory(
                                    internalOptionCategories?.option_categories.find(
                                      (internalOptionCategory: InternalOptionCategory) =>
                                        internalOptionCategory.id === id
                                    ) ?? null
                                  )
                                }
                                value={createOptionSelectedOptionCategory?.id}
                              >
                                <Stack>
                                  {internalOptionCategories?.option_categories.map(
                                    (internalOptionCategory: InternalOptionCategory) =>
                                      isQuickMode === internalOptionCategory.quick_mode && (
                                        <Radio value={internalOptionCategory.id}>
                                          {`${internalOptionCategory.name}${
                                            internalOptionCategory.quick_mode
                                              ? " (Quick Mode)"
                                              : " (Normal Mode)"
                                          }`}
                                        </Radio>
                                      )
                                  )}
                                </Stack>
                              </RadioGroup>
                            </Stack>
                            <Textarea
                              placeholder="Prompt"
                              value={createOptionPrompt}
                              onChange={(e) => setCreateOptionPrompt(e.target.value)}
                            />
                            <Textarea
                              placeholder="Negative prompt"
                              value={createOptionNegativePrompt}
                              onChange={(e) => setCreateOptionNegativePrompt(e.target.value)}
                            />
                            <Checkbox
                              isChecked={createOptionNSFW}
                              onChange={(e) => setCreateOptionNSFW(e.target.checked)}
                            >
                              NSFW
                            </Checkbox>
                            {isQuickMode && (
                              <>
                                <HStack alignItems="center">
                                  <Text mr={2}>
                                    Settings (If not {"{}"}, override the entire style settings):
                                  </Text>
                                  <Textarea
                                    placeholder="Settings (If not {}, override the entire style settings)"
                                    value={createOptionSettings}
                                    onChange={(e) => setCreateOptionSettings(e.target.value)}
                                  />
                                </HStack>
                                <Flex w="100%" direction="column">
                                  <Text fontWeight="bold">ControlNet images</Text>
                                  <Dropzone
                                    files={controlnetImageUploadFiles}
                                    setFiles={setControlnetImageUploadFiles}
                                  />
                                </Flex>
                              </>
                            )}
                            <Button colorScheme="blue" onClick={() => handleInternalOptionCreate()}>
                              Create option
                            </Button>
                          </VStack>
                        </Flex>
                      )}
                    </Flex>
                  </ModalBody>
                  <ModalFooter>
                    <Button colorScheme="green" mr={3} onClick={() => handleInternalStyleUpdate()}>
                      Update
                    </Button>
                    <Button
                      colorScheme="red"
                      mr={3}
                      onClick={() => handleInternalStyleDelete(false)}
                    >
                      Delete
                    </Button>
                    <Button
                      colorScheme="red"
                      mr={3}
                      onClick={() => handleInternalStyleDelete(true)}
                    >
                      Force Delete
                    </Button>
                    <Button
                      colorScheme="blue"
                      mr={3}
                      onClick={() => {
                        internalStyleOnClose();
                        setSelectedInternalStyle(null);
                      }}
                    >
                      Close
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            )}
          </Flex>
          {!isInternalModelCategoriesLoading && (
            <Flex direction="column" w="30%">
              <Text fontWeight="semibold" fontSize="20px" mt="16px">
                Create style
              </Text>
              <VStack spacing="4" align="stretch" mt="5">
                <Input
                  placeholder="Name"
                  value={createStyleName}
                  onChange={(e) => setCreateStyleName(e.target.value)}
                />
                <Stack direction="row" alignItems="center">
                  <Text fontWeight={700}>Model category</Text>
                  <RadioGroup
                    onChange={setCreateStyleModelCategoryName}
                    value={createStyleModelCategoryName}
                  >
                    <Stack>
                      {internalModelCategories?.model_categories.map(
                        (internalModelCategory: InternalModelCategory) => (
                          <Radio value={internalModelCategory.name}>
                            {`${internalModelCategory.name}`}
                          </Radio>
                        )
                      )}
                    </Stack>
                  </RadioGroup>
                </Stack>
                <Textarea
                  placeholder="Prompt"
                  value={createStylePrompt}
                  onChange={(e) => setCreateStylePrompt(e.target.value)}
                />
                <Textarea
                  placeholder="Negative prompt"
                  value={createStyleNegativePrompt}
                  onChange={(e) => setCreateStyleNegativePrompt(e.target.value)}
                />
                <Textarea
                  placeholder="Settings (JSON String)"
                  value={createStyleSettings}
                  onChange={(e) => setCreateStyleSettings(e.target.value)}
                />
                <Button colorScheme="blue" onClick={() => handleInternalStyleCreate()}>
                  Create
                </Button>
              </VStack>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default StylePanel;
